import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import { toast } from "react-toastify";
import { DATE_TIME_FORMAT_INPUT } from "./constants";

export { default as cn } from "clsx";

export const stringfy = (payload) => JSON.stringify(payload);

// Escape string for safe use in RegExp
const escapeRegExp = (str) => str?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") ?? "";

// Remove field path like "bus_cards.0.bus_number" from server messages
const cleanServerMessage = (fieldKey, msg) => {
  if (!msg) return msg;

  let out = msg;

  // Original key like "bus_cards.0.bus_number"
  const key = escapeRegExp(fieldKey);

  // Alternate form used in some UIs: "bus_cards[0].bus_number"
  const altKey = escapeRegExp(fieldKey.replace(/\.(\d+)\./g, "[$1]."));

  // Patterns to strip leading "The <key>" or any occurrence of the key
  const patterns = [
    new RegExp(`^\\s*The\\s+${key}\\s*`, "i"),
    new RegExp(`\\s*${key}\\s*`, "gi"),
    new RegExp(`^\\s*The\\s+${altKey}\\s*`, "i"),
    new RegExp(`\\s*${altKey}\\s*`, "gi"),
  ];

  patterns.forEach((p) => {
    out = out.replace(p, "");
  });

  // Tidy whitespace
  out = out.replace(/\s{2,}/g, " ").trim();

  // Capitalize first letter if needed
  if (out && out[0] === out[0]?.toLowerCase()) {
    out = out[0].toUpperCase() + out.slice(1);
  }

  return out || msg; // Fallback if we stripped everything
};

// Function to convert Western Arabic numerals to Arabic-Indic numerals
export const toArabicNumerals = (str) => {
  if (!str) return "-";
  const arabicNumerals = "٠١٢٣٤٥٦٧٨٩";
  const westernNumerals = "0123456789";

  return (
    String(str)?.replace(/[0-9]/g, (digit) => {
      return arabicNumerals[westernNumerals.indexOf(digit)];
    }) || "-"
  );
};

// Function to format date based on language
export const formatDate = (
  date,
  language = "ae",
  format = "DD - MMMM YYYY"
) => {
  if (!date) return "-";

  const dayjsDate = dayjs(date);

  if (language === "ae" || language === "ar") {
    // Set Arabic locale
    const arabicFormattedDate = dayjsDate.locale("ar").format(format);
    // Convert to Arabic-Indic numerals
    return arabicFormattedDate
  } else {
    // Set English locale
    return dayjsDate.locale("en").format(format);
  }
};

export const getAvatarLetters = (str) => {
  const name = str?.split(" ") ?? [];
  return (name?.[0]?.[0] ?? "") + (name?.[1]?.[0] ?? "");
};

/**
 * Handles server validation errors for react-hook-form
 * @param {Object} err - The error object from axios
 * @param {Function} setError - react-hook-form's setError function
 * @param {Function} t - Translation function (optional)
 * @param {String} fallbackMessage - Fallback error message (optional)
 */
export const handleFormErrors = (
  err,
  setError,
  t = null,
  fallbackMessage = "An error occurred"
) => {
  if (
    err?.response?.status === 422 &&
    err?.response?.data?.errors &&
    setError
  ) {
    const serverErrors = err.response.data.errors;
    delete serverErrors.message;

    // Set field-specific errors
    Object.keys(serverErrors).forEach((fieldName) => {
      const errorMessages = serverErrors[fieldName];
      if (errorMessages && errorMessages.length > 0) {
        // Convert Laravel dot notation to react-hook-form array notation
        // "bus_cards.0.bus_name" → "bus_cards.0.bus_name" (keep dots, don't use brackets)
        // React Hook Form accepts both notations, but dots are more reliable
        const formFieldName = fieldName;

        const rawMessage = errorMessages[0];
        const cleaned = cleanServerMessage(fieldName, rawMessage);

        setError(formFieldName, {
          type: "server",
          message: cleaned,
        });
      }
    });

    // Don't show a general error toast here — the global query/mutation
    // error handler already displays one to avoid duplicate notifications.

    return true; // Indicate errors were handled
  }

  return false; // Indicate no validation errors
};
// Handle other types of errors
const handleGenericError = (err, t, fallbackMessage) => {
  const errorMessage =
    err?.response?.data?.message ||
    err?.message ||
    (t ? t(fallbackMessage) : fallbackMessage);

  return toast.error(errorMessage);
};

/**
 * Converts a date to Arabic day name
 * @param {string|Date|dayjs} dateInput - Date input (ISO string, Date object, or dayjs object)
 * @returns {string} Arabic day name
 */
export const getDayNameInArabic = (dateInput) => {
  if (!dateInput) return "";

  const dayjsDate = dayjs(dateInput);
  const dayOfWeek = dayjsDate.day(); // 0 = Sunday, 1 = Monday, etc.

  const arabicDays = [
    "الأحد", // Sunday
    "الاثنين", // Monday
    "الثلاثاء", // Tuesday
    "الأربعاء", // Wednesday
    "الخميس", // Thursday
    "الجمعة", // Friday
    "السبت", // Saturday
  ];

  return arabicDays[dayOfWeek] || "";
};

export const proxyHandler_undefined = (value) => ({
  get(target, prop, receiver) {
    return Reflect.get(target, prop, receiver) ?? value;
  },
});

export const stringifyKeys = (obj, keys) => {
  const normalized = { ...obj };

  keys.forEach((key) => {
    if (normalized[key] !== undefined && normalized[key] !== null) {
      normalized[key] = String(normalized[key]);
    }
  });

  return normalized;
};

export const filterObject = (obj) => {
  return Object?.fromEntries(
    Object.entries(obj || {}).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
  );
};
export const PickKeyPairs = (obj, keys) => {
  return Object?.fromEntries?.(
    Object?.entries?.(obj ?? {})?.filter(([key]) =>
      keys.some((k) => k === key)
    ) ?? []
  );
};
export const getDublication = (arr, key) => {
  const groups = Object.groupBy(
    arr.map((v, i) => ({ v, i })),
    (x) => x.v[key]
  );

  return Object.values(groups)
    .filter((group) => group.length > 1 && group[0].v[key] != null)
    .flatMap((group) => group.map((x) => x.i));
};
export const addQToItem = (item) => ({
  q: [
    ...Object.values(item),
    ...Object.values(item).map(toArabicNumerals),
  ].join(","),
  ...item,
});
export const dateFormat = (date) =>
  date ? dayjs(date).format(DATE_TIME_FORMAT_INPUT) : null;

export const mapObjectValues = (str) => {
  const keys = str.split(".");
  return keys.reduce((acc, key) => {
    return acc[key];
  }, {});
};


/**
 * Download file from blob response
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};


export const joinValues = (values, separator = " - ") => {
  return Array.isArray(values) ? values.join(separator) : values;
};
export const filter = (values) => {
  return Array.isArray(values) ? values.filter(Boolean) : values;
}